import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { notify, TYPE, DURATION } from "@8x8/oxygen-toaster";

import PageTitle from "@/react/components/PageTitle";
import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";

import { CustomerCard } from "@/react/features/customer";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageLayout from "@/react/features/styled/PageLayout";

import { useGetDBServiceQuery } from "@/react/services/api";

import { useCountMapArray } from "@/react/collections";
import { useErrorNotification } from "@/react/notifications";

import columnDefinition from "./columnDefinition";
import PbxBLFCard from "./PbxBLFCard";
import schema from "./schema";
import { MemoryInfoButton, PbxCardsContainer } from "./styled";

const columns = columnDefinition();

const PBX_MEMORY_INFO_MESSAGE =
  "Please contact the Operations team to request a memory increase. Prepare to provide the PBX name and reason for the change.";

export const BLFCountPage = () => {
  const cid = useSelector(customerIdSelector);

  const { data, isFetching, error, refetch } = useGetDBServiceQuery({
    queryName: "blfcount",
    queryArguments: [cid],
  });

  // This is so that we auto-reload the table when the customer changes
  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, [cid]);

  useErrorNotification(error);

  const { table, filters, state, mutations } = useTable(
    data,
    columns,
    schema,
    schema.fields.blfId.spec.key
  );
  const noMutations = useMemo(
    () => filterTableMutations(mutations),
    [mutations]
  );

  // This is an array where each element is a pair of pbx name and the number blf counts for that pbx
  // Each pbx name appears only once in the array
  // The array is sorted in descending order of the number of blf counts
  const pbxBlfCountMapArray = useCountMapArray(data, "name");

  const handleInfoButton = (event) =>
    notify({
      type: TYPE.warning,
      content: PBX_MEMORY_INFO_MESSAGE,
      duration: DURATION.INFINITE,
    });

  return (
    <PageLayout>
      <CustomerCard />
      <PageContentContainer>
        <PageTitle>BLF (Busy Lamp Fields) Count Checker</PageTitle>
        {!isFetching &&
          (pbxBlfCountMapArray.length > 0 ? (
            <>
              <PbxCardsContainer>
                {pbxBlfCountMapArray.map((pbxCountEntry) => (
                  <PbxBLFCard
                    pbxName={pbxCountEntry[0]}
                    blfCount={pbxCountEntry[1]}
                  />
                ))}
              </PbxCardsContainer>
              <MemoryInfoButton size="small" onClick={handleInfoButton}>
                PBX Memory Increase ...
              </MemoryInfoButton>
            </>
          ) : (
            <p>No BLF data for selected customer</p>
          ))}
        <TableWrapper>
          <TableComponent
            title="BLF Count Check"
            schema={schema}
            isLoading={isFetching}
            onRefresh={refetch}
            table={table}
            filters={filters}
            mutations={noMutations}
            state={state}
          />
        </TableWrapper>
      </PageContentContainer>
    </PageLayout>
  );
};

export default BLFCountPage;
